import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Building2, Phone, Mail, ExternalLink, CheckCircle2, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function LeadResults() {
  const { leadId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to the new view-matches page which has all features
    navigate(`/view-matches/${leadId}`, { replace: true });
  }, [leadId, navigate]);

  // Show loading while redirecting
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-red-600 border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading your matches...</p>
      </div>
    </div>
  );
}

// Old implementation below - kept for reference
function LeadResultsOld() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactingCompany, setContactingCompany] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, [leadId]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}/companies`);
      setCompanies(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to load companies");
      setLoading(false);
    }
  };

  const handleContact = async (companyId) => {
    setContactingCompany(companyId);
    
    try {
      const response = await axios.post(`${API}/contact-click`, {
        lead_id: leadId,
        company_id: companyId
      });

      const company = companies.find(c => c.id === companyId);
      
      // Open email and phone options
      toast.success("Contact information revealed!");
      
      // Show contact modal
      const contactInfo = response.data;
      const message = `Contact ${company.company_name}:\n\nPhone: ${contactInfo.phone}\nEmail: ${contactInfo.email}`;
      
      if (window.confirm(message + "\n\nWould you like to call them now?")) {
        window.location.href = `tel:${contactInfo.phone}`;
      }
    } catch (error) {
      console.error("Error tracking contact:", error);
      toast.error("Failed to get contact information");
    } finally {
      setContactingCompany(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-red-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Finding the best companies for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Building2 className="h-8 w-8 text-blue-600" />
            <span className="text-2xl font-bold text-white">Top Prop</span>
          </a>
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="border-indigo-200 hover:bg-indigo-50"
          >
            Return to Home
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {/* Success Message */}
        <div className="max-w-4xl mx-auto mb-12 text-center">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Thank You for Your Submission!
          </h1>
          <p className="text-lg text-slate-600 mb-4">
            We've matched you with the top property management companies in your area.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-slate-700">
            <p className="font-medium mb-2">📋 Disclaimer:</p>
            <p>
              Your contact information has been shared with the property management companies listed below. 
              They may reach out to you directly to discuss your property management needs. 
              By submitting the form, you consent to being contacted by these companies.
            </p>
          </div>
        </div>

        {/* Companies List */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6">
            Top Property Management Companies in Your Area
          </h2>

          {companies.length === 0 ? (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-12 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-600">
                  No companies found in your area yet. We'll notify you when new companies join!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6" data-testid="companies-list">
              {companies.map((company) => (
                <Card
                  key={company.id}
                  className="bg-white border-slate-200 hover:shadow-lg transition-all"
                  data-testid={`company-${company.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-2xl mb-2">{company.company_name}</CardTitle>
                        <div className="flex items-center gap-2 text-slate-600 mb-3">
                          <MapPin className="h-4 w-4" />
                          <span>{company.address}</span>
                        </div>
                        {company.description && (
                          <CardDescription className="text-base">
                            {company.description}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => handleContact(company.id)}
                        disabled={contactingCompany === company.id}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`contact-${company.id}`}
                      >
                        {contactingCompany === company.id ? (
                          "Loading..."
                        ) : (
                          <>
                            <Phone className="h-4 w-4 mr-2" />
                            Contact Them
                          </>
                        )}
                      </Button>
                      
                      {company.website && (
                        <Button
                          onClick={() => window.open(company.website, "_blank")}
                          variant="outline"
                          className="border-slate-300"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Website
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="max-w-4xl mx-auto mt-12 text-center">
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                What Happens Next?
              </h3>
              <ul className="text-left text-slate-700 space-y-2 max-w-2xl mx-auto">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>The companies above have received your information and will reach out to you</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>You can also contact them directly using the buttons above</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Compare their services and choose the best fit for your property</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Building2 className="h-6 w-6" />
            <span className="text-xl font-bold">Top Prop</span>
          </div>
          <p className="text-slate-400">© 2025 Top Prop. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
